import { useCallback, useState } from 'react';
import { useQuery, UseQueryResult } from 'react-query';
import { useSelector } from 'react-redux';
import {
  getAccountabilityBands,
  getAccountabilityFilters,
  getAccountabilityReport,
  downloadAccountabilityExport,
} from '@apis/accountability';
import { getUserId } from '../../../reducers/user/userReducer';
import {
  AccountabilityFilters,
  AccountabilityReport,
  BandsResponse,
  ReportQuery,
  ReportRow,
} from './types';

/** Stable, order-independent cache key fragment for a query. */
const queryKeyFor = (query: ReportQuery) => [
  query.start,
  query.end,
  query.bucket ?? 'auto',
  [...query.schoolIds].sort((a, b) => a - b).join(','),
  query.curriculumId ?? '',
  query.subjectId ?? '',
  query.teachersMode,
  query.districtId ?? '',
];

/**
 * The signed-in user's id leads every key below. Logging out keeps the query
 * cache, so without it the next account in the same tab would match the
 * previous account's cached report and see it while its own one loads.
 */
const useUserId = (): number | undefined => useSelector(getUserId);

const unwrap = (result: any) => {
  if (result?.response?.data) return result.response.data;
  throw new Error(result?.error?.message || 'Could not load the accountability report.');
};

/** Toolbar options. Keyed on district and mode only, so other filters never refetch them. */
export const useAccountabilityFilters = ({
  districtId,
  teachersMode,
}: Pick<ReportQuery, 'districtId' | 'teachersMode'>): UseQueryResult<
  AccountabilityFilters,
  Error
> => {
  const userId = useUserId();
  return useQuery<AccountabilityFilters, Error>(
    ['accountabilityFilters', userId, districtId ?? '', teachersMode],
    async () => unwrap(await getAccountabilityFilters({ districtId, teachersMode })),
    {
      enabled: Boolean(userId),
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
};

export const useAccountabilityReport = (
  query: ReportQuery,
): UseQueryResult<AccountabilityReport, Error> => {
  const userId = useUserId();
  return useQuery<AccountabilityReport, Error>(
    ['accountabilityReport', userId, ...queryKeyFor(query)],
    async () => unwrap(await getAccountabilityReport(query)),
    {
      enabled: Boolean(userId && query.start && query.end),
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
};

/**
 * One teacher's sections. `enabled` lets a closed "By section" row keep its
 * cached count without fetching.
 */
export const useTeacherSections = (
  query: ReportQuery,
  teacherKey: string | null,
  enabled = true,
): UseQueryResult<ReportRow[], Error> => {
  const userId = useUserId();
  return useQuery<ReportRow[], Error>(
    ['accountabilitySections', userId, teacherKey, ...queryKeyFor(query)],
    async () => {
      const data = unwrap(await getAccountabilityReport(query, teacherKey as string));
      return data.rows as ReportRow[];
    },
    {
      enabled: Boolean(userId && enabled && teacherKey && query.start && query.end),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
};

export const useEntityBands = (
  query: ReportQuery,
  entityKey: string | null,
): UseQueryResult<BandsResponse, Error> => {
  const userId = useUserId();
  return useQuery<BandsResponse, Error>(
    ['accountabilityBands', userId, entityKey, ...queryKeyFor(query)],
    async () => unwrap(await getAccountabilityBands(query, entityKey as string)),
    {
      enabled: Boolean(userId && entityKey && query.start && query.end),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
};

/** Client-side filename: a blob download ignores the response's
 * Content-Disposition header, so the name has to be derived here. Keying it
 * off the requested window keeps exports of different ranges from
 * overwriting each other in the downloads folder. */
const exportFilename = (query: ReportQuery): string =>
  `accountability-${query.start}-to-${query.end}.csv`;

const exportErrorMessage = (result: { error?: unknown } | undefined): string => {
  const error = result?.error;
  if (Array.isArray(error) && error.length) return String(error[0]);
  if (typeof error === 'string' && error) return error;
  return 'Could not export the accountability report. Please try again.';
};

/**
 * Downloads the accountability CSV and saves it client-side. A plain
 * `<a href="/api/...">` cannot do this: it sends no Authorization header
 * (the JWT is attached by an axios interceptor) and would otherwise hit the
 * dev server's SPA history fallback instead of the API.
 */
export const useAccountabilityExport = (): {
  exportCsv: (query: ReportQuery) => Promise<void>;
  isExporting: boolean;
  error: string | null;
} => {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportCsv = useCallback(async (query: ReportQuery) => {
    setIsExporting(true);
    setError(null);
    try {
      const result = await downloadAccountabilityExport(query);
      if (!result?.response?.data) {
        throw new Error(exportErrorMessage(result));
      }
      const url = window.URL.createObjectURL(result.response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', exportFilename(query));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Could not export the accountability report. Please try again.',
      );
    } finally {
      setIsExporting(false);
    }
  }, []);

  return { exportCsv, isExporting, error };
};
